import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Paginator } from 'src/app/models/paginator';
import { Trademark, TrademarkSynchronizationStatus } from 'src/app/models/trademark';
import { SelectionModel } from '@angular/cdk/collections';
import _ from 'lodash';
import { synchronizationFailureKey } from 'src/app/helpers/trademark-synchronization.helper';

// The only optional column: the column chooser adds or removes it, every other column stays
const SYNCHRONIZATION_COLUMN = 'synchronization';

@Component({
  selector: 'app-trademarks-table',
  templateUrl: './trademarks-table.component.html',
  styleUrls: ['./trademarks-table.component.scss']
})
export class TrademarksTableComponent implements OnInit {
  loading: boolean = false;
  done: boolean = false;
  noDocs: boolean = false;
  @Input() trademarks: Paginator<Trademark> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
    sort: 'name'
  };

  @Input() select?: boolean = false;
  @Input() selection = new SelectionModel<Trademark>(true, []);
  @Output() selectionChange = new EventEmitter<SelectionModel<Trademark>>();

  @Input() displayedColumns: string[] = ['name', 'identifier', 'countryOfDesignation', 'niceClassification', 'status', 'representation', 'actions'];
  // Key under which the column chooser remembers the user's choice; no persistence when absent
  @Input() columnsStorageKey?: string;
  @Input() dataSource: MatTableDataSource<Trademark> = new MatTableDataSource<Trademark>([]);
  @Input() button?: {
    icon: string;
    class: string;
    action: Function
  };

  @Output() sort = new EventEmitter<any>();
  @Output() page = new EventEmitter<PageEvent>();

  showSynchronization: boolean = false;
  visibleColumns: string[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly translate: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.displayedColumns = (this.select ? ['select', ...this.displayedColumns] : this.displayedColumns);
    this.showSynchronization = this.readSynchronizationChoice();
    this.updateVisibleColumns();
  }

  toggleSynchronization() {
    this.showSynchronization = !this.showSynchronization;
    this.saveSynchronizationChoice();
    this.updateVisibleColumns();
  }

  private updateVisibleColumns() {
    const columns = this.displayedColumns.filter(column => column !== SYNCHRONIZATION_COLUMN);
    if (this.showSynchronization) {
      const actionsIndex = columns.indexOf('actions');
      columns.splice(actionsIndex >= 0 ? actionsIndex : columns.length, 0, SYNCHRONIZATION_COLUMN);
    }
    this.visibleColumns = columns;
  }

  private storageKey(): string | undefined {
    return this.columnsStorageKey ? `uzai-trademarks-table-columns-${this.columnsStorageKey}` : undefined;
  }

  private readSynchronizationChoice(): boolean {
    const key = this.storageKey();
    if (!key) return false;
    try {
      const stored = JSON.parse(localStorage.getItem(key) || '{}');
      return stored?.[SYNCHRONIZATION_COLUMN] === true;
    } catch {
      return false;
    }
  }

  private saveSynchronizationChoice() {
    const key = this.storageKey();
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify({ [SYNCHRONIZATION_COLUMN]: this.showSynchronization }));
    } catch {
      // Storage unavailable (private browsing, quota): the choice only lasts for this page
    }
  }

  isSynchronizationSuccess(trademark: Trademark): boolean {
    return trademark.synchronization?.status === TrademarkSynchronizationStatus.SUCCESS;
  }

  formatSynchronizationDate(date?: string): string {
    return date ? moment(date).format('ll') : '-';
  }

  synchronizationTooltip(trademark: Trademark): string {
    const synchronization = trademark.synchronization;
    if (!synchronization) {
      return this.translate.instant('SYNC.NEVER_SYNCHRONIZED');
    }
    if (synchronization.status === TrademarkSynchronizationStatus.SUCCESS) {
      return this.translate.instant('SYNC.STATE_SUCCESS');
    }
    const lines = [this.translate.instant(synchronizationFailureKey(synchronization.failure))];
    if (synchronization.lastSuccessDate) {
      lines.push(this.translate.instant('SYNC.LAST_SUCCESS', { date: this.formatSynchronizationDate(synchronization.lastSuccessDate) }));
    }
    return lines.join(' · ');
  }

  hasPageSelection() {
    return this.trademarks.docs.some(row => this.isSelected(row))
  }

  isSelected(tm: Trademark) {
    return this.selection.selected.map(doc => doc._id).includes(tm._id)
  }

  isAllSelected() {
    return this.trademarks.docs.every(tm => this.isSelected(tm));
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      const difference = _.differenceBy(this.selection.selected, this.trademarks.docs, '_id')
      this.selection.setSelection(...difference)
      this.selectionChange.emit(this.selection);
      return;
    }
    this.selection.setSelection(...[...this.selection.selected, ...this.trademarks.docs.filter(tm => !this.isSelected(tm))])
    this.selectionChange.emit(this.selection);
  }

  toggle(tm: Trademark) {
    if (this.isSelected(tm)) {
      this.selection.setSelection(...this.selection.selected.filter(doc => doc._id !== tm._id))
    } else {
      this.selection.setSelection(...[...this.selection.selected, tm])
    }
    this.selectionChange.emit(this.selection);
  }

  async handlePage(event: PageEvent) {
    this.page.emit(event)
  }

  onSortChange(event: any) {
    if (['asc', 'desc'].includes(event.direction)) {
      this.sort.emit(event)
    }
  }

  action(trademark: Trademark) {
    if (this.button && this.button.action) {
      this.button.action(trademark);
    } else {
      this.router.navigateByUrl(`/trademarks/${trademark._id}`)
    }
  }

  toTrademark(trademark: Trademark) {
    this.router.navigateByUrl(`/trademarks/${trademark._id}`)
  }


}
